import { useContext, useCallback, useMemo } from "react"
import { useState } from "react"
import { createContext } from "react"
import PitchContext from "./Pitch"
import TimezoneContext from "./Timezone"
import LocaleContext from "./Locale"
import { format } from "date-fns"
import { toZonedTime } from "date-fns-tz"
import fetchPDFFromService from "../utils/fetchPDF"
import UnitSystemContext from "./UnitSystem"
import GridModeContext from "./GridMode"
import useRawState from "../hooks/useRawState"
import useBridge from "../hooks/useBridge"
import useBootRatings from "../hooks/useBootRatings"

const PitchIndexPdfData = createContext()
export default PitchIndexPdfData

export function PitchIndexPdfDataProvider({ ...props }) {
    const [indexResults, setIndexResults] = useState()
    const [referenceTimestamp, setReferenceTimestamp] = useState()
    // mirrors whatever the page currently has selected/dragged - IndexOverviewCard and
    // TrainingGoalZones report their live state here so the PDF is a snapshot of what
    // the user is actually looking at, not an independently recomputed default
    const [selectedTrainingGoalId, setSelectedTrainingGoalId] = useState(null)
    const [trainingGoalPercentage, setTrainingGoalPercentage] = useState(50)
    // same idea: PitchIndexPage's visibility toggle for expired data, mirrored here so
    // the PDF hides/dims expired results the same way the page did at export time
    const [showExpired, setShowExpired] = useState(true)

    const { data: pitch } = useContext(PitchContext)
    const { browserTimezone } = useContext(TimezoneContext)
    const { locale, datefnsLocale } = useContext(LocaleContext)
    const { unitSystem } = useContext(UnitSystemContext)
    const { selectedMode: zoneResultsMode } = useContext(GridModeContext)
    const { data: bootTypes, loading: bootTypesLoading } = useBridge('/api/current/frontend-bridge/boot-types')

    const selectedTrainingGoalIndex = useMemo(() => {
        if (!indexResults || selectedTrainingGoalId == null) return
        return indexResults.find(ind => ind.indexCategory.id === selectedTrainingGoalId)
    }, [indexResults, selectedTrainingGoalId])

    // indexResults already carries each zone's own measurements (indexZoneResults),
    // but the pitch template (used to lay those zones out / find goal-mouths) is only
    // referenced by IRI, so it needs its own fetch
    const { data: trainingGoalTemplate, isBusy: trainingGoalTemplateLoading } = useRawState(
        selectedTrainingGoalIndex?.indexCategory?.pitchTemplate?.['@id']
    )

    // boot advice - same data prep as BootAdviceModal, needed by BootIndexResultsScatterPlot
    const bootIndex = useMemo(() => (
        indexResults?.find(ind => ind.indexCategory.indexCategoryGroup === 'boot')
    ), [indexResults])

    const { bootRatings, isBusy: bootRatingsLoading } = useBootRatings(bootIndex)

    const requestPayload = useMemo(() => {
        if (!pitch || !indexResults || !referenceTimestamp) return
        // don't ship a snapshot while any of its pieces are still in flight - otherwise
        // clicking download early can silently omit the training-goal template or boot
        // ratings from the exported PDF
        if (bootTypesLoading || trainingGoalTemplateLoading || bootRatingsLoading) return

        const tZ = pitch?.venue?.timeZone || browserTimezone
        const date = format(toZonedTime(referenceTimestamp * 1000, tZ), "PP", {
            locale: datefnsLocale,
        })

        // formatted here (not in the pdf-service) because datefnsLocale loses its
        // functions over JSON - only its plain fields would survive - and because the
        // webapp already has the pitch's real timezone/locale on hand, same as
        // AnalysisDate does for this exact field on the page itself
        const enrichedIndexResults = indexResults.map(ind => ({
            ...ind,
            formattedLatestTestDate: ind.latestTestTimestamp ?
                format(toZonedTime(ind.latestTestTimestamp * 1000, tZ), "PP", { locale: datefnsLocale })
                : undefined,
        }))

        return {
            report: {
                reportType: "pitchIndexPage",
                formattedFromDate: date,
                reporterTz: tZ,
                locale: locale,
                unitSystem,
                zoneResultsMode,
            },
            pitch,
            indexResults: enrichedIndexResults,
            referenceTimestamp,
            trainingGoalTemplate,
            selectedTrainingGoalId,
            trainingGoalPercentage,
            bootTypes,
            bootRatings,
            showExpired,
        }
    }, [
        pitch,
        browserTimezone,
        datefnsLocale,
        locale,
        indexResults,
        referenceTimestamp,
        unitSystem,
        zoneResultsMode,
        trainingGoalTemplate,
        selectedTrainingGoalId,
        trainingGoalPercentage,
        bootTypes,
        bootRatings,
        showExpired,
        bootTypesLoading,
        trainingGoalTemplateLoading,
        bootRatingsLoading,
    ])

    const dataReady = !!requestPayload

    const fetchPDF = useCallback(async () => {
        if (!dataReady) return

        try {
            await fetchPDFFromService(
                requestPayload,
                `${pitch.name}-index-${requestPayload.report.formattedFromDate}.pdf`,
            )
        } catch (e) {
            throw new Error("Could not fetch PDF", { cause: e })
        }
    }, [requestPayload, dataReady, pitch])

    return (
        <PitchIndexPdfData.Provider
            value={{
                setIndexResults,
                setReferenceTimestamp,
                setSelectedTrainingGoalId,
                setTrainingGoalPercentage,
                setShowExpired,
                dataReady,
                fetchPDF,
            }}
            {...props}
        />
    )
}
